import { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router'
import { CheckCircle2, Minus, Plus, ShoppingBag, Sparkles, FlaskConical, Droplet, ShieldAlert, ArrowRight } from 'lucide-react'
import { useProducts } from '../../context/ProductsContext'
import { api } from '../../api/client'
import { useCart } from '../../context/CartContext'
import SampleRequestForm from './RequestQuote'
import SampleReceived from './SampleReceived'
import Curve from '../../assets/Curve.svg'
import ProductCard from '../Products/ProductCard'
import CtaButton from '../ui/CtaButton'

const defaultVolumeTiers = [{ units: 300, price: 0, label: 'Base price' }]
const tabIcons = {
  'Clinical Benefits': Sparkles,
  'Key Ingredients': FlaskConical,
  'How to use': Droplet,
  'Safety Information': ShieldAlert,
}

const YOU_MAY_ALSO_LIKE_COUNT = 4

const ProductDetailed = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const { products, loading } = useProducts()
  const product = products.find((p) => String(p.id) === id)

  const packSizes = product?.packSizes || []
  const volumeTiers = product?.volumeTiers?.length ? product.volumeTiers : [
    { ...defaultVolumeTiers[0], units: product?.moqUnits || defaultVolumeTiers[0].units, price: Number(product?.price) || 0 },
  ]
  const tabs = useMemo(() => product?.tabs && typeof product.tabs === 'object' ? product.tabs : {}, [product?.tabs])

  const [activeImage, setActiveImage] = useState(0)
  const [selectedPack, setSelectedPack] = useState(0)
  const [selectedVolume, setSelectedVolume] = useState(0)
  const [batches, setBatches] = useState(1)
  const [activeTab, setActiveTab] = useState(Object.keys(tabs)[0] || '')
  const [sampleStage, setSampleStage] = useState('config')
  const [sampleSubmitting, setSampleSubmitting] = useState(false)
  const [sampleSubmitError, setSampleSubmitError] = useState('')

  // Actually persists the sample request — without this, the "Request
  // Sample Kit" flow only ever changed local UI state and the business
  // would never see a single request that came through it.
  const handleSampleSubmit = async (formData) => {
    setSampleSubmitting(true)
    setSampleSubmitError('')
    try {
      await api.createSubmission({
        type: 'sample_request',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        productId: product?.id,
        productTitle: product?.title,
        payload: {
          location: formData.location,
          regNumber: formData.regNumber,
          address: formData.address,
        },
      })
      setSampleStage('received')
    } catch (err) {
      setSampleSubmitError(err.message || 'Something went wrong — please try again.')
    } finally {
      setSampleSubmitting(false)
    }
  }

  // Reset selection state whenever the product id changes, so stale indices
  // from a previous product (e.g. selectedPack = 3 on a product that only has
  // 1 pack size) can't cause an out-of-bounds crash. Using the "adjust state
  // during render" pattern keeps the component in sync without an effect.
  const [prevProductId, setPrevProductId] = useState(id)
  if (prevProductId !== id) {
    setPrevProductId(id)
    setActiveImage(0)
    setSelectedPack(0)
    setSelectedVolume(0)
    setBatches(1)
    setActiveTab(Object.keys(tabs)[0])
    setSampleStage('config')
  }

  // Keep the browser title in sync with the product being viewed.
  useEffect(() => {
    if (product) {
      document.title = `${product.title} | Kamakhya`
    }
  }, [product])

  useEffect(() => {
    const names = Object.keys(tabs)
    if (!names.includes(activeTab)) setActiveTab(names[0] || '')
  }, [tabs, activeTab])

  const volume = volumeTiers[selectedVolume]
  const discountPercent = Number(product?.discountPercent || 0)
  const discountedUnitPrice = volume?.price != null && Number(volume.price) > 0 ? Number(volume.price) * (1 - discountPercent / 100) : null
  const totalUnits = volume?.units ? volume.units * batches : 0
  const orderTotal = discountedUnitPrice != null ? (discountedUnitPrice * volume.units * batches).toFixed(2) : null
  const canOrder = Boolean(product && discountedUnitPrice != null)

  const handleAddToOrder = () => {
    if (!product || discountedUnitPrice == null) return

    addToCart(product, {
      packSize: activePack?.size || 'Standard',
      unitsPerPack: volume?.units || 0,
      batches,
      price: Number.isFinite(Number(discountedUnitPrice)) ? Number(discountedUnitPrice) : 0,
      sku: product.sku,
      shippingCost: product.shippingCost,
    })

    navigate('/checkout')
  }

  // Recommendations: prefer same-category products, excluding the current
  // one; if there aren't enough, pad with any other products so the strip
  // never looks sparse.
  const recommendedProducts = product
    ? (() => {
      const sameCategory = products.filter(
        (p) => p.id !== product.id && p.category === product.category
      )
      if (sameCategory.length >= YOU_MAY_ALSO_LIKE_COUNT) {
        return sameCategory.slice(0, YOU_MAY_ALSO_LIKE_COUNT)
      }
      const others = products.filter(
        (p) => p.id !== product.id && !sameCategory.includes(p)
      )
      return [...sameCategory, ...others].slice(0, YOU_MAY_ALSO_LIKE_COUNT)
    })()
    : []

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FCF9F2]">
        <p className="text-gray-400">Loading product…</p>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#FCF9F2]">
        <p className="text-gray-600">Product not found.</p>
        <Link to="/products" className="text-[#2E3192] font-medium underline">
          Back to Catalogue
        </Link>
      </div>
    )
  }

  // Guard against a stale index if data length ever shrinks between renders
  const activePack = packSizes[selectedPack] ?? packSizes[0]

  return (
    <div className="bg-[#FCF9F2] min-h-screen font-poppins">

      <div className="max-w-6xl mx-auto px-5 sm:px-10 py-10 grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-8">
        {/* Image Section  */}
        <div className="lg:sticky lg:top-24 lg:self-start min-[641px]:max-[1023px]:max-w-md min-[641px]:max-[1023px]:mx-auto">
          <div className="rounded-lg overflow-hidden  border border-gray-200 mb-4">
            <img
              src={product.images?.[activeImage] || product.image || 'https://placehold.co/1200x1200/FCF9F2/2E3192?text=Kamakhya'}
              alt={product.title}
              className="w-full h-[420px] object-cover"
              onError={(e) => {
                e.currentTarget.src = 'https://placehold.co/1200x1200/FCF9F2/2E3192?text=Kamakhya'
                e.currentTarget.onerror = null
              }}
            />
          </div>
          <div className="flex gap-3">
            {(product.images || [product.image]).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                aria-label={`View image ${i + 1} of ${product.title}`}
                aria-pressed={activeImage === i}
                className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${activeImage === i ? 'border-orange-400' : 'border-transparent'
                  }`}
              >
                <img
                  src={img || 'https://placehold.co/240x240/FCF9F2/2E3192?text=Kamakhya'}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://placehold.co/240x240/FCF9F2/2E3192?text=Kamakhya'
                    e.currentTarget.onerror = null
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Info + configurator */}
        <div className="bg-white rounded-lg p-8 border border-gray-100  shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
          <p className="tracking-widest font-poppins text-[#E38F2E] mb-1">{product.tag}</p>
          <div className='h-0.5 w-20 mb-5 bg-[#E38F2E] rounded-full' />
          <h2 className="text-3xl tracking-wider font-playfair font-bold text-[#2E3192] mb-2">{product.title}</h2>
          <p className="text-sm text-[#666666] mb-4">{product.desc}</p>

          {product.notes?.length > 0 && (
            <div className="mb-6">
              <p className="font-semibold text-gray-800 mb-2">Product Notes</p>
              <ul className="space-y-1.5">
                {product.notes.map((note, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-poppins text-[#666666]">
                    <CheckCircle2 className="w-4 h-4 text-[#CCA466]" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sampleStage === 'form' ? (
            <SampleRequestForm
              product={product}
              onBack={() => setSampleStage('config')}
              onSubmitted={handleSampleSubmit}
              submitting={sampleSubmitting}
              submitError={sampleSubmitError}
            />
          ) : sampleStage === 'received' ? (
            <SampleReceived product={product} onSubmitAnother={() => setSampleStage('form')} />
          ) : (
            <>
            {/* Pack sizes are shown only when configured in the CMS. */}
            {packSizes.length > 0 && <div className="mb-6">
              <p className="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span className="w-6 h-6 rounded-full font-poppins bg-[#2E3192] text-white text-xs flex items-center justify-center">1</span>
                Select Pack Size
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {packSizes.map((pack, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedPack(i)}
                    aria-pressed={selectedPack === i}
                    className={`text-left p-3 rounded-xl border relative ${selectedPack === i ? 'border-orange-400 bg-orange-50' : 'border-gray-200'
                      }`}
                  >
                    {selectedPack === i && (
                      <CheckCircle2 className="w-4 h-4 text-[#E38F2E] absolute top-3 right-3" />
                    )}
                    <p className="font-serif font-semibold text-[#2E3192]">{pack.size}</p>
                    <p className="text-xs text-[#E38F2E] mb-1">{pack.label}</p>
                    <p className="text-xs text-gray-500">{pack.desc}</p>
                  </button>
                ))}
              </div>
            </div>}

            {/* Order volume */}
            <div className="mb-6">
              <p className="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span className="w-6 h-6 rounded-full bg-[#2E3192] text-white text-xs flex items-center justify-center">2</span>
                Select Order Volume
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {volumeTiers.map((tier, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedVolume(i)}
                    aria-pressed={selectedVolume === i}
                    className={`text-left p-3 rounded-xl border border-[#E38F2E] relative ${selectedVolume === i ? 'border-[#E38F2E] bg-orange-50' : 'border-gray-200'
                      }`}
                  >
                    {selectedVolume === i && (
                      <CheckCircle2 className="w-4 h-4 text-orange-400 absolute top-3 right-3" />
                    )}
                    {discountPercent > 0 && (
                      <span className="absolute top-2 right-2 text-xs font-bold text-white"
                        style={{ background: '#CCA466', padding: '2px 6px', borderRadius: '4px' }}>
                        -{discountPercent}%
                      </span>
                    )}
                    <p className="font-serif font-semibold text-[#2E3192]">
                      {tier.custom ? `${tier.units.toLocaleString()}+ units` : `${tier.units} units`}
                    </p>
                    <p className="text-xs font-poppins text-[#E38F2E] mb-1" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      {tier.custom ? tier.label : (
                        <>
                          <span>NRs {(tier.price * (1 - discountPercent / 100)).toFixed(2)}/unit</span>
                          {discountPercent > 0 && (
                            <span style={{ color: '#999', textDecoration: 'line-through', fontSize: '12px' }}>
                              NRs {tier.price.toFixed(2)}
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">{tier.custom ? 'Custom pricing' : tier.label}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Batches */}
            <div className="mb-6">
              <p className="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span className="w-6 h-6 rounded-full bg-[#2E3192] text-white text-xs flex items-center justify-center">3</span>
                Select Number of Batches
              </p>
              <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setBatches((b) => Math.max(1, b - 1))}
                    aria-label="Decrease number of batches"
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="font-bold text-lg" aria-live="polite">{batches}</span>
                  <button
                    onClick={() => setBatches((b) => b + 1)}
                    aria-label="Increase number of batches"
                    className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  {volume?.units} units × {batches} batch{batches > 1 ? 'es' : ''}
                </p>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-[#191B50] text-white rounded-xl p-5 mb-5">
              {product?.sku && <p className="text-xs text-orange-200 mb-3">SKU: {product.sku}</p>}
              <div className="flex justify-between mb-3 bg-[#191850]">
                <div>
                  <p className="text-orange-300 text-xs mb-1">Order Configuration</p>
                  <p className="font-serif font-semibold">{activePack?.size || 'Standard'}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{discountedUnitPrice != null ? `NRs ${discountedUnitPrice.toFixed(2)}` : '—'}</p>
                  {discountPercent > 0 && <p className="text-xs text-orange-200">{product.discountLabel || `${discountPercent}% discount applied`}</p>}
                  <p className="text-xs text-orange-200">per unit</p>
                </div>
              </div>
              <hr className="border-white/20 mb-3" />
              <div className="flex justify-between text-xs text-orange-200 mb-3"><span>Shipping per batch</span><span>NRs. {Number(product?.shippingCost || 0).toFixed(2)}</span></div>
              {discountPercent > 0 && volume?.price != null && (
                <div className="flex justify-between mb-3 p-3 rounded-lg" style={{ background: 'rgba(204, 164, 102, 0.2)' }}>
                  <div>
                    <p className="text-orange-300 text-xs mb-1">You Save</p>
                    <p className="font-bold text-[#CCA466]">
                      NRs {((volume.price - discountedUnitPrice) * volume.units * batches).toFixed(2)}
                      {' '}
                      <span className="text-xs font-normal text-orange-200">
                        ({discountPercent}% off)
                      </span>
                    </p>
                  </div>
                </div>
              )}
              <div className="flex justify-between bg-[#191850]">
                <div>
                  <p className="text-orange-300 text-xs mb-1">Total units</p>
                  <p className="font-bold">{totalUnits}</p>
                  <p className="text-xs text-orange-200">{volume?.units} x {batches}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-orange-200 mb-1">order total</p>
                  <p className="font-bold text-orange-300 text-lg">
                    {orderTotal ? `NRs ${Number(orderTotal).toLocaleString()}` : 'Request Quote'}
                  </p>
                  {discountPercent > 0 && volume?.price != null && (
                    <p className="text-xs text-white/50 line-through mt-1">
                      NRs {(volume.price * volume.units * batches).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-15 max-sm:grid-cols-1">
              <CtaButton onClick={handleAddToOrder} disabled={!canOrder} style={{ width: "100%" }} title={!canOrder ? "Add a base price or volume price in the CMS before accepting orders." : undefined}>
                <ShoppingBag className="w-4 h-4" />
                {canOrder ? 'Add to Order' : 'Price required to order'}
              </CtaButton>
              <button
                onClick={() => setSampleStage('form')}
                className="flex items-center justify-center border-2  border-[#CCA466] font-poppins text-[#E38F2E] text-xs rounded-lg py-2 px-12 font-medium whitespace-nowrap cursor-pointer"
              >
                Request Sample Kit
              </button>
            </div>
            </>
          )}
        </div>
      </div>

      {Object.keys(tabs).length > 0 && (
        <section className="w-full bg-white">
          <div className="max-w-6xl mx-auto px-5 sm:px-10 py-14">
            <div className="flex gap-5 border-b border-[#D7DAE4] bg-[#F5F5FA] mb-6 justify-start sm:justify-evenly items-center pt-3 overflow-x-auto whitespace-nowrap">
              {Object.keys(tabs).map((tabName) => {
                const Icon = tabIcons[tabName] || Sparkles
                return <button key={tabName} onClick={() => setActiveTab(tabName)} aria-pressed={activeTab === tabName} className={`flex items-center gap-2 pb-3 px-1 whitespace-nowrap text-sm font-medium border-b-2 ${activeTab === tabName ? 'border-[#E38F2E] text-[#E38F2E]' : 'border-transparent text-gray-500'}`}><Icon className="w-4 h-4" />{tabName}</button>
              })}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {(tabs[activeTab] || []).map((point, index) => <div key={index} className="flex items-start gap-3 rounded-xl border border-[#E8D6BA] bg-[#FCFAF7] px-5 py-4"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#CCA466]" /><span className="text-indigo-900">{point}</span></div>)}
            </div>
          </div>
        </section>
      )}

      {/* Business Support Section  */ }
  <div className='w-full bg-[#151642] py-14'>
    <div className='max-w-6xl mx-auto px-5 sm:px-10 flex flex-col lg:flex-row justify-between items-center gap-8'>
      {/* Left Section */}
      <div className='flex flex-col text-white gap-3 max-w-2xl'>
        <h2 className='font-playfair text-2xl md:text-3xl leading-tight'>
          Need product documentation before you configure an order?
        </h2>
        <p className='text-white/70 font-poppins text-sm '>
          For verified wholesale buyers, our business team can guide the appropriate technical documentation, packaging compatibility and sample pathway.
        </p>
      </div>

      {/* Right Section - button */}
      <div className='shrink-0'>
        <Link
          to={"/bulk-quote"}
          className='inline-flex items-center gap-2 bg-[#CCA466] px-10 py-2 rounded-lg text-[#151642] font-poppins hover:bg-[#b8925a] text-sm transition-colors'
        >
          Contact Business Support
          <ArrowRight className='w-4 h-4' />
        </Link>
      </div>
    </div>
  </div>

  {/* You May Also Like Section  */ }
  <div>
    <div className="flex flex-col items-center justify-center py-14 sm:py-16 lg:py-20">
      <h2 className='text-[#E38F2E] uppercase'>You May Also Like</h2>
      <h1 className='text-center text-3xl text-[#2E3192] tracking-wide font-playfair'>Find Your Next Beauty Essential</h1>
      <div><img src={Curve} alt="" /></div>
      <p className='font-poppins text-center text-[#666666] max-w-3xl  mx-auto'>
        Explore more skincare and beauty favorites, thoughtfully selected to complement your routine and elevate your everyday care.
      </p>
    </div>

    <div className="px-5 sm:px-10 lg:px-20">
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <clipPath id="figureCardImageClip" clipPathUnits="objectBoundingBox">
            <path d="M0 0 L1 0 L1 0.8158 C1 0.8256 0.9957 0.8347 0.9885 0.8402 C0.8579 0.938 0.6864 1 0.4987 1 C0.3122 1 0.1419 0.9388 0.0117 0.8423 C0.0043 0.8368 0 0.8276 0 0.8178 Z" />
          </clipPath>
        </defs>
      </svg>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 min-[641px]:max-[1023px]:gap-7">
        {recommendedProducts.map((recProduct) => (
          <ProductCard key={recProduct.id} product={recProduct} />
        ))}
      </div>

      <div className="flex justify-center mt-10 pb-14 sm:pb-16 lg:pb-20">
        <Link
          to="/products"
          className="flex items-center gap-2 border border-[#2E3192] text-[#2E3192] rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#2E3192] hover:text-white transition"
        >
          Explore All Collections
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>

    </div >
  )
}

export default ProductDetailed
